import '../css/Breadcrumbs.less';

import React from 'react';
import {NavLink} from 'react-router-dom';
import {Breadcrumb} from 'semantic-ui-react';
import config from '../config';

import {AccessOptions} from '../util/permissions';
import * as Url from '../util/url';
import * as links from '../util/urls';
import * as String from '../util/string';
import LegacyWBIcon from './elements/LegacyWBIcon';
import ProjectAccess, {ProjectAccessProject} from './ProjectAccess';
import makeComp from '../util/profiler';
import {useFormatGalleryTag, galleryTagFromQS} from '../util/gallery';
import {useSelector} from '../state/hooks';

interface BreadcrumbsProps {
  entityName: string;
  projectName?: string;
  tab?: string;
  filePath?: string;
  project?: ProjectAccessProject;
  updateProjectAccess?: (access: AccessOptions) => void;
  sweepName?: string;
  sweepDisplayName?: string;
  runName?: string;
  runDisplayName?: string;
  runState?: string;

  groupName?: string;

  reportNameAndId?: string;
  authorName?: string;

  artifactTypeName?: string;
  artifactCollectionName?: string;
  artifactCommitHash?: string;
  artifactTab?: string;

  pageType: string;
  isBenchmark?: boolean;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = makeComp(
  ({
    entityName,
    projectName,
    tab,
    filePath,
    project,
    updateProjectAccess,
    sweepName,
    sweepDisplayName,
    runName,
    runDisplayName,
    runState,
    groupName,
    reportNameAndId,
    authorName,
    artifactTypeName,
    artifactCollectionName,
    artifactCommitHash,
    artifactTab,
    pageType,
    isBenchmark: propsIsBenchmark,
  }) => {
    const isBenchmark = pageType === 'benchmark' || propsIsBenchmark;
    const isOnPrem = config.ENVIRONMENT_IS_PRIVATE;

    const projObjType =
      groupName != null
        ? 'groups'
        : sweepName != null
        ? 'sweeps'
        : reportNameAndId != null
        ? 'reports'
        : runName != null
        ? 'runs'
        : undefined;
    const projObjName =
      groupName || groupName || sweepName || reportNameAndId || runName;
    let reportName = '';
    let reportId = '';
    if (reportNameAndId) {
      try {
        const parsed = Url.parseNameAndID(reportNameAndId || '');
        reportName = parsed.name;
        reportId = parsed.id;
      } catch {
        // do nothing
      }
    }

    // Special characters in international report names are stripped from the url.
    // Need to access the report view in redux to fetch the display name for nav breadcrumbs.
    const view = useSelector(state => {
      if (projObjType !== 'reports') {
        return null;
      }
      return Object.values(state.views.views).find(v => v.id === reportId);
    });
    reportName = view?.displayName ?? reportName;

    const tabName = tab === 'reportlist' ? 'reports' : tab;
    const filePathParts = Url.parseRunTabPath(filePath);

    const galleryTag = galleryTagFromQS();
    const formattedGalleryTag = useFormatGalleryTag(galleryTag ?? '');
    if (projectName != null && reportNameAndId && galleryTag) {
      // HAX: this works with host/src/report.ts to ensure the app does not override custom SEO breadcrumbs
      const seoBreadcrumbs: Array<{
        name: string;
        path: string;
      }> = (window as any).__WB_SEO_BREADCRUMBS;
      if (seoBreadcrumbs != null) {
        const withoutDividers = seoBreadcrumbs.map(({name, path}) => (
          <Breadcrumb.Section key={`${name}-${path}`}>
            <NavLink to={path}>{name}</NavLink>
          </Breadcrumb.Section>
        ));
        const withDividers = [];
        let i = 0;
        for (const b of withoutDividers) {
          withDividers.push(b);
          withDividers.push(
            <Breadcrumb.Divider
              key={`divider-${i}`}
              icon={<LegacyWBIcon name="next" />}
            />
          );
          i++;
        }
        withDividers.splice(-1, 1);
        return (
          <div className="nav-breadcrumbs-wrapper">
            <Breadcrumb className="nav-breadcrumbs" size="small">
              {withDividers}
            </Breadcrumb>
          </div>
        );
      }

      if (projectName == null) {
        return (
          <div className="nav-breadcrumbs-wrapper">
            <Breadcrumb className="nav-breadcrumbs" size="small">
              <Breadcrumb.Section>
                <NavLink to={`/${entityName}`}>{entityName}</NavLink>
              </Breadcrumb.Section>
            </Breadcrumb>
          </div>
        );
      }
      return (
        <div className="nav-breadcrumbs-wrapper">
          <Breadcrumb className="nav-breadcrumbs" size="small">
            <Breadcrumb.Section>
              <NavLink to="/">Home</NavLink>
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
            <Breadcrumb.Section>
              <NavLink to={links.reportGallery()}>Reports</NavLink>
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
            <Breadcrumb.Section>
              <NavLink to={links.reportGallery({tag: galleryTag})}>
                {formattedGalleryTag}
              </NavLink>
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
            <Breadcrumb.Section>
              <NavLink
                to={`${links.reportView({
                  entityName,
                  projectName: projectName ?? '',
                  reportID: reportId,
                  reportName,
                })}${window.location.search}`}>
                {reportName}
              </NavLink>
            </Breadcrumb.Section>
          </Breadcrumb>
        </div>
      );
    }

    return (
      <div className="nav-breadcrumbs-wrapper">
        <Breadcrumb className="nav-breadcrumbs" size="small">
          {isBenchmark && (
            <>
              <Breadcrumb.Section>
                {isOnPrem ? (
                  'Benchmarks'
                ) : (
                  <a href="/site/benchmarks">Benchmarks</a>
                )}
              </Breadcrumb.Section>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
            </>
          )}
          <Breadcrumb.Section>
            <NavLink to={`/${entityName}`}>{entityName}</NavLink>
          </Breadcrumb.Section>
          {projectName != null && (
            <>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink to={`/${entityName}/projects`}>Projects</NavLink>
              </Breadcrumb.Section>
            </>
          )}

          {projectName != null && (
            <>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.project({
                    entityName,
                    name: projectName,
                  })}>
                  {projectName}
                </NavLink>
                {project && updateProjectAccess && (
                  <ProjectAccess
                    project={project}
                    compact={true}
                    updateProjectAccess={updateProjectAccess}
                  />
                )}
              </Breadcrumb.Section>
            </>
          )}
          {projectName != null && sweepName != null && (
            <React.Fragment>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.projectSweeps({
                    entityName,
                    name: projectName,
                  })}>
                  Sweeps
                </NavLink>
              </Breadcrumb.Section>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.sweep({
                    entityName,
                    projectName,
                    sweepName,
                  })}>
                  {sweepDisplayName}
                </NavLink>
              </Breadcrumb.Section>
            </React.Fragment>
          )}
          {projectName != null && runName != null && (
            <React.Fragment>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.project({
                    entityName,
                    name: projectName,
                  })}>
                  Runs
                </NavLink>
              </Breadcrumb.Section>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.run({
                    entityName,
                    projectName,
                    name: runName,
                  })}>
                  {runDisplayName || runName}
                </NavLink>
              </Breadcrumb.Section>
            </React.Fragment>
          )}
          {projectName != null && groupName != null && (
            <React.Fragment>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>Run Groups</Breadcrumb.Section>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.runGroup({
                    entityName,
                    projectName,
                    name: groupName,
                  })}>
                  {groupName}
                </NavLink>
              </Breadcrumb.Section>
            </React.Fragment>
          )}
          {projectName != null && reportNameAndId != null && (
            <React.Fragment>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.reportList({
                    entityName,
                    projectName,
                  })}>
                  Reports
                </NavLink>
              </Breadcrumb.Section>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.reportView({
                    entityName,
                    projectName,
                    reportID: reportId,
                    reportName,
                  })}>
                  {reportName}
                </NavLink>
              </Breadcrumb.Section>
            </React.Fragment>
          )}
          {projectName != null && tabName != null && tab != null && (
            <React.Fragment>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.projectObjTab(
                    entityName,
                    projectName,
                    projObjType,
                    projObjName,
                    tab
                  )}>
                  {String.capitalizeFirst(tabName)}
                </NavLink>
              </Breadcrumb.Section>
            </React.Fragment>
          )}
          {projectName != null && artifactTypeName != null && (
            <React.Fragment>
              <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
              <Breadcrumb.Section>
                <NavLink
                  to={links.artifactType({
                    entityName,
                    projectName,
                    artifactTypeName,
                  })}>
                  {artifactTypeName}
                </NavLink>
              </Breadcrumb.Section>
            </React.Fragment>
          )}
          {projectName != null &&
            artifactTypeName != null &&
            artifactCollectionName != null && (
              <React.Fragment>
                <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
                <Breadcrumb.Section>
                  <NavLink
                    to={links.artifactSequence({
                      entityName,
                      projectName,
                      artifactTypeName,
                      artifactSequenceName: artifactCollectionName,
                    })}>
                    {artifactCollectionName}
                  </NavLink>
                </Breadcrumb.Section>
              </React.Fragment>
            )}
          {projectName != null &&
            artifactTypeName != null &&
            artifactCollectionName != null &&
            artifactCommitHash != null && (
              <React.Fragment>
                <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
                <Breadcrumb.Section>
                  <NavLink
                    to={links.artifact({
                      entityName,
                      projectName,
                      artifactTypeName,
                      artifactSequenceName: artifactCollectionName,
                      artifactCommitHash,
                    })}>
                    {artifactCommitHash.slice(0, 9)}
                  </NavLink>
                </Breadcrumb.Section>
              </React.Fragment>
            )}
          {projectName != null &&
            artifactTypeName != null &&
            artifactCollectionName != null &&
            artifactCommitHash != null &&
            artifactTab != null && (
              <React.Fragment>
                <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
                <Breadcrumb.Section>
                  <NavLink
                    to={links.artifactTab({
                      entityName,
                      projectName,
                      artifactTypeName,
                      artifactSequenceName: artifactCollectionName,
                      artifactCommitHash,
                      tabName: artifactTab,
                    })}>
                    {artifactTab}
                  </NavLink>
                </Breadcrumb.Section>
              </React.Fragment>
            )}
          {projectName != null &&
            artifactTypeName != null &&
            artifactCollectionName != null &&
            artifactCommitHash != null &&
            filePathParts != null && (
              <>
                {filePathParts.map((p, i) => (
                  <React.Fragment key={'file-path-' + p}>
                    <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
                    <Breadcrumb.Section>
                      <NavLink
                        to={links.artifactFile({
                          entityName,
                          projectName,
                          artifactTypeName,
                          artifactSequenceName: artifactCollectionName,
                          artifactCommitHash,
                          path: filePathParts.slice(0, i + 1),
                        })}>
                        {p}
                      </NavLink>
                    </Breadcrumb.Section>
                  </React.Fragment>
                ))}
              </>
            )}
          {runName != null && filePathParts != null && (
            <>
              {filePathParts.map((p, i) => (
                <React.Fragment key={'file-path-' + p}>
                  <Breadcrumb.Divider icon={<LegacyWBIcon name="next" />} />
                  <Breadcrumb.Section>
                    <NavLink
                      to={links.runTabFile(
                        entityName,
                        projectName,
                        runName!,
                        filePathParts.slice(0, i + 1)
                      )}>
                      {p}
                    </NavLink>
                  </Breadcrumb.Section>
                </React.Fragment>
              ))}
            </>
          )}
        </Breadcrumb>
      </div>
    );
  },
  {id: 'Breadcrumbs.1', memo: true}
);
